import React, { useState, useEffect } from 'react';
import { db, Exercise } from '../database';
import { seedExerciseLibrary } from '../utils/seedData';

interface ExerciseSelectorProps {
  onSelect: (exercise: Exercise) => void;
  onClose: () => void;
  excludeIds?: number[];
}

const ExerciseSelector: React.FC<ExerciseSelectorProps> = ({ 
  onSelect, 
  onClose, 
  excludeIds = [] 
}) => {
  const [exercises, setExercises] = useState<Exercise[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newExercise, setNewExercise] = useState({
    name: '',
    category: 'Push' as string,
    description: ''
  });

  useEffect(() => {
    loadExercises();
  }, []);

  const loadExercises = async () => {
    try {
      setLoading(true);
      const allExercises = await db.exercises.orderBy('name').toArray();
      setExercises(allExercises);
    } catch (error) {
      console.error('Failed to load exercises:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSeedExercises = async () => {
    try {
      await seedExerciseLibrary();
      await loadExercises();
    } catch (error) {
      console.error('Failed to seed exercises:', error);
      alert('Failed to add exercise library. Check console for details.');
    }
  };

  const handleCreateExercise = async () => {
    if (!newExercise.name.trim()) return;

    try {
      const now = new Date();
      const exerciseId = await db.exercises.add({
        name: newExercise.name.trim(),
        category: newExercise.category,
        description: newExercise.description.trim() || undefined,
        createdAt: now,
        updatedAt: now
      });

      // Create the full exercise object to pass to onSelect
      const createdExercise: Exercise = {
        id: exerciseId,
        name: newExercise.name.trim(),
        category: newExercise.category,
        description: newExercise.description.trim() || undefined,
        createdAt: now,
        updatedAt: now
      };

      // Refresh the exercise list
      await loadExercises();
      
      // Automatically select the new exercise and close
      onSelect(createdExercise);
      
    } catch (error) {
      console.error('Failed to create exercise:', error);
      alert('Failed to create exercise. Check console for details.');
    }
  };

  const resetCreateForm = () => {
    setNewExercise({
      name: '',
      category: 'Push',
      description: ''
    });
    setShowCreateForm(false);
  };

  const categories = ['All', 'Push', 'Pull', 'Legs', 'Core', 'Cardio', 'Functional', 'Machine'];
  
  const availableExercises = exercises.filter(ex => !excludeIds.includes(ex.id!));
  
  const filteredExercises = availableExercises.filter(ex => {
    // Apply search filter
    const matchesSearch = searchTerm === '' || 
      ex.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (ex.description && ex.description.toLowerCase().includes(searchTerm.toLowerCase()));
    
    // Apply category filter
    const matchesCategory = selectedCategory === 'All' || ex.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg max-w-2xl w-full max-h-96 overflow-y-auto">
          <div className="p-8 text-center">
            <div className="text-gray-500">Loading exercises...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-900">
            {showCreateForm ? 'Create New Exercise' : `Select Exercise (${filteredExercises.length})`}
          </h3>
          <button
            onClick={showCreateForm ? resetCreateForm : onClose}
            className="text-gray-500 hover:text-gray-700 text-xl"
          >
            {showCreateForm ? '←' : '✕'}
          </button>
        </div>

        {/* Create Exercise Form */}
        {showCreateForm ? (
          <div className="p-4 border-b space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Exercise Name *
              </label>
              <input
                type="text"
                value={newExercise.name}
                onChange={(e) => setNewExercise({...newExercise, name: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g., Incline Barbell Press"
                autoFocus
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category *
              </label>
              <select
                value={newExercise.category}
                onChange={(e) => setNewExercise({...newExercise, category: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {categories.filter(cat => cat !== 'All').map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description (optional)
              </label>
              <textarea
                value={newExercise.description}
                onChange={(e) => setNewExercise({...newExercise, description: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g., Chest exercise on inclined bench"
                rows={2}
              />
            </div>
            
            <div className="flex space-x-2 relative z-10">
              <button
                onClick={handleCreateExercise}
                disabled={!newExercise.name.trim()}
                className="flex-1 bg-blue-600 text-white py-2 rounded-lg disabled:bg-gray-300 disabled:cursor-not-allowed cursor-pointer hover:bg-blue-700"
                style={{ pointerEvents: 'auto' }}
              >
                Create & Add Exercise
              </button>
              <button
                onClick={resetCreateForm}
                className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg cursor-pointer hover:bg-gray-400"
                style={{ pointerEvents: 'auto' }}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Search and Category Filter */}
            {exercises.length > 0 && (
              <div className="p-4 border-b space-y-3">
                {/* Search Input */}
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search exercises..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full p-2 pl-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <div className="absolute left-2.5 top-2.5 text-gray-400">
                    🔍
                  </div>
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      className="absolute right-2.5 top-2.5 text-gray-400 hover:text-gray-600"
                    >
                      ✕
                    </button>
                  )}
                </div>
                
                {/* Category Filter */}
                <div className="flex flex-wrap gap-2">
                  {categories.map(category => (
                    <button
                      key={category}
                      onClick={() => setSelectedCategory(category)}
                      className={`px-3 py-1 rounded-full text-sm ${
                        selectedCategory === category
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-700'
                      }`}
                    >
                      {category}
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {/* Create Exercise Button */}
            <div className="px-4 py-2 bg-gray-50 border-b">
              <button
                onClick={() => setShowCreateForm(true)}
                className="w-full bg-green-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-green-700"
              >
                + Create New Exercise
              </button>
            </div>
          </>
        )}

        {/* Exercise List - only show when not creating */}
        {!showCreateForm && (
          <div className="flex-1 overflow-y-auto p-4">
            {exercises.length === 0 ? (
              <div className="text-center text-gray-500 py-8 space-y-4">
                <p>No exercises available. Load the exercise library!</p>
                <button
                  onClick={handleSeedExercises}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm"
                >
                  📚 Load Exercise Library (140+ exercises)
                </button>
              </div>
            ) : filteredExercises.length === 0 ? (
              <div className="text-center text-gray-500 py-8 space-y-2">
                <p>No exercises match your search and filters.</p>
                <div className="space-x-2">
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      className="text-blue-600 text-sm"
                    >
                      Clear search
                    </button>
                  )}
                  {selectedCategory !== 'All' && (
                    <button
                      onClick={() => setSelectedCategory('All')}
                      className="text-blue-600 text-sm"
                    >
                      Show all categories
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredExercises.map((exercise) => (
                  <button
                    key={exercise.id}
                    onClick={() => onSelect(exercise)}
                    className="w-full bg-white border rounded-lg p-4 text-left hover:border-blue-300 hover:bg-blue-50 transition-colors"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900">{exercise.name}</h4>
                        {exercise.category && (
                          <span className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded mt-1">
                            {exercise.category}
                          </span>
                        )}
                        {exercise.description && (
                          <p className="text-gray-600 text-sm mt-2">{exercise.description}</p>
                        )}
                      </div>
                      <div className="text-blue-600 ml-4">
                        +
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Footer - only show when not creating */}
        {!showCreateForm && (
          <div className="p-4 border-t bg-gray-50">
            <button
              onClick={onClose}
              className="w-full bg-gray-300 text-gray-700 py-2 rounded"
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExerciseSelector;